import type { ListWatchCriterion } from '@/lib/backend-api';

/**
 * The criteria picker, grouped.
 *
 * Fourteen checkboxes in one flat column is a wall — so they are grouped by
 * the question they answer, each group collapsible, with a filter box and a
 * "recommended" preset for the common case. `recommended` marks the five
 * that matter to most people watching a portfolio: who owns it, who runs
 * it, whether it is in trouble, and whether the numbers moved.
 *
 * Order within a group is by how often the change actually happens, so the
 * useful boxes are the ones nearest the top.
 */
export const CRITERION_GROUPS: {
  id: string;
  labelKey: string;
  labelFallback: string;
  criteria: {
    id: ListWatchCriterion;
    labelKey: string;
    labelFallback: string;
    hintKey: string;
    hintFallback: string;
    /** Hint for a company watch, where the list wording would be wrong. */
    companyHintKey?: string;
    recommended?: boolean;
  }[];
}[] = [
  {
    id: 'risk',
    labelKey: 'lists.watch.groups.risk',
    labelFallback: 'Risk & legal status',
    criteria: [
      {
        id: 'legal_status_change',
        labelKey: 'lists.watch.criteria.legalStatus',
        labelFallback: 'Insolvency or liquidation',
        hintKey: 'lists.watch.criteria.legalStatusHint',
        hintFallback:
          'The company becomes insolvent, is dissolved, enters liquidation — or recovers.',
        recommended: true,
      },
    ],
  },
  {
    id: 'people',
    labelKey: 'lists.watch.groups.people',
    labelFallback: 'Ownership & management',
    criteria: [
      {
        id: 'shareholder_change',
        labelKey: 'lists.watch.criteria.shareholder',
        labelFallback: 'Shareholders',
        hintKey: 'lists.watch.criteria.shareholderHint',
        hintFallback: 'A new shareholder list is filed.',
        recommended: true,
      },
      {
        id: 'management_change',
        labelKey: 'lists.watch.criteria.management',
        labelFallback: 'Management',
        hintKey: 'lists.watch.criteria.managementHint',
        hintFallback: 'A managing director or board member joins or leaves.',
        recommended: true,
      },
    ],
  },
  {
    id: 'financials',
    labelKey: 'lists.watch.groups.financials',
    labelFallback: 'Financials',
    criteria: [
      {
        id: 'filing_change',
        labelKey: 'lists.watch.criteria.filing',
        labelFallback: 'New annual accounts',
        hintKey: 'lists.watch.criteria.filingHint',
        hintFallback: 'A new financial statement is published.',
        recommended: true,
      },
      {
        id: 'revenue_change',
        labelKey: 'lists.watch.criteria.revenue',
        labelFallback: 'Revenue',
        hintKey: 'lists.watch.criteria.revenueHint',
        hintFallback: 'The reported revenue figure changes.',
        recommended: true,
      },
      {
        id: 'profit_change',
        labelKey: 'lists.watch.criteria.profit',
        labelFallback: 'Profit',
        hintKey: 'lists.watch.criteria.profitHint',
        hintFallback: 'The reported profit or loss changes.',
      },
      {
        id: 'assets_change',
        labelKey: 'lists.watch.criteria.assets',
        labelFallback: 'Total assets',
        hintKey: 'lists.watch.criteria.assetsHint',
        hintFallback:
          'The balance-sheet total changes — reported by far more companies than revenue.',
      },
      {
        id: 'employee_change',
        labelKey: 'lists.watch.criteria.employees',
        labelFallback: 'Employees',
        hintKey: 'lists.watch.criteria.employeesHint',
        hintFallback: 'The reported headcount changes.',
      },
    ],
  },
  {
    id: 'activity',
    labelKey: 'lists.watch.groups.activity',
    labelFallback: 'Business activity',
    criteria: [
      {
        id: 'tender_change',
        labelKey: 'lists.watch.criteria.tender',
        labelFallback: 'Public tenders',
        hintKey: 'lists.watch.criteria.tenderHint',
        hintFallback: 'The company wins a public contract or appears as a buyer.',
      },
      {
        id: 'industry_change',
        labelKey: 'lists.watch.criteria.industry',
        labelFallback: 'Industry',
        hintKey: 'lists.watch.criteria.industryHint',
        hintFallback: 'The classified industry or WZ/NACE code changes.',
      },
      {
        id: 'website_change',
        labelKey: 'lists.watch.criteria.website',
        labelFallback: 'Website',
        hintKey: 'lists.watch.criteria.websiteHint',
        hintFallback: 'The company website we resolved changes.',
      },
    ],
  },
  {
    id: 'profile',
    labelKey: 'lists.watch.groups.profile',
    labelFallback: 'Company profile',
    criteria: [
      {
        id: 'name_change',
        labelKey: 'lists.watch.criteria.name',
        labelFallback: 'Name',
        hintKey: 'lists.watch.criteria.nameHint',
        hintFallback: 'The company is renamed.',
        companyHintKey: 'lists.watch.criteria.nameHintCompany',
      },
      {
        id: 'address_change',
        labelKey: 'lists.watch.criteria.address',
        labelFallback: 'Registered seat',
        hintKey: 'lists.watch.criteria.addressHint',
        hintFallback: 'The company moves to another city or postcode.',
      },
      {
        id: 'segment_change',
        labelKey: 'lists.watch.criteria.segment',
        labelFallback: 'Segment change',
        hintKey: 'lists.watch.criteria.segmentHint',
        hintFallback:
          'A company moves between the segments defined on this list.',
      },
    ],
  },
  {
    // The three document-arrival criteria. Categorically different from
    // everything above: those compare two observed *values*, these fire when
    // a document you can open actually lands, and carry its file id.
    //
    // They must be listed here even though the wizard was built for lists.
    // A company watch created by the star button subscribes to exactly these
    // three, so a picker that could not show them would silently clear them
    // the first time someone opened it and pressed save.
    id: 'documents',
    labelKey: 'lists.watch.groups.documents',
    labelFallback: 'New documents',
    criteria: [
      {
        id: 'new_balance_sheet',
        labelKey: 'lists.watch.criteria.newBalanceSheet',
        labelFallback: 'Annual accounts filed',
        hintKey: 'lists.watch.criteria.newBalanceSheetHint',
        hintFallback:
          'A new Unternehmensregister filing is available to open — not just a changed figure.',
        recommended: true,
      },
      {
        id: 'new_cd_entry',
        labelKey: 'lists.watch.criteria.newCdEntry',
        labelFallback: 'New register entry',
        hintKey: 'lists.watch.criteria.newCdEntryHint',
        hintFallback:
          'Anything the Handelsregister records — every Eintragung, whatever it changed.',
      },
      {
        id: 'new_gesellschafterliste',
        labelKey: 'lists.watch.criteria.newGesellschafterliste',
        labelFallback: 'Shareholder list filed',
        hintKey: 'lists.watch.criteria.newGesellschafterlisteHint',
        hintFallback:
          'A new Gesellschafterliste document is on file, whether or not the owners changed.',
      },
    ],
  },
];

export const ALL_CRITERIA = CRITERION_GROUPS.flatMap((group) => group.criteria);
export const RECOMMENDED_CRITERIA = ALL_CRITERIA.filter((c) => c.recommended).map(
  (c) => c.id,
);
